<script module lang="ts">
  import type { PermissionState } from "@kontextwerk/control-web-frame/shared/logic/authorization"
  import type { WorkOrdersViewFrameContext } from "./context"
</script>

<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import type { OrganizationFrame } from "@kontextwerk/control-web-client"
  import OrgRootBrandingMount from "@kontextwerk/control-web-frame/shared/modules/organization-branding-mount"
  import { getOrganizationWorkspace } from "@kontextwerk/control-web-frame/shared/logic/organization-workspace"
  import PageTemplate from "@kontextwerk/web-sdk/ui/layout/PageTemplate.svelte"
  import type { RecordDetailConfig } from "@kontextwerk/web-sdk/ui/layout/recordDetail/types"
  import { buildWorkOrderHeader, workOrderRecordIcon } from "./_logic/header"
  import { normalizePath } from "./_shared/_logic/work-order-page-helpers"
  import { location, spaNavigate } from "@kontextwerk/web-sdk/core"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { workOrderStatusLabels } from "../../../../../../_shared/_logic/outreach-presentation"
  import { formatAddress } from "../../../../../../_shared/_logic/form-state"
  import { fetchOrganizationFrame } from "../../../../../../api/kehrblatt/organizations"
  import { resolveOrganizationSubscriptionRestriction } from "../../../../_shared/_logic/free-trial-experience"
  import { NO_PERMISSIONS } from "../../../../../_shared/_logic/permissions-model"
  import { updateAssistContext } from "../../../../_shared/_logic/assist-index"
  import {
    registerEventCallback,
    unregisterEventCallback,
  } from "../../../../../_shared/_logic/eventbus"
  import { ensureOrgBillingPlan, orgPlanLookup } from "../../../../_shared/_logic/org-billing-plan"
  import WorkOrderHeaderActions from "./_modules/work-order-header-actions"
  import SchedulePlanningPage from "./pages/SchedulePlanning"
  import { buildWorkOrderDetailViewNavigation, buildWorkOrderDetailViewPaths } from "./navigation"
  import { getKehrblattControlAuthContext } from "../../../../../_shared/_logic/auth-context"
  import { archivePresentation } from "../../../../_shared/_logic/archive-presentation"
  interface Props {
    organizationId: string
    organizationSlug?: string | null
    organizationResolvedId?: string | null
    workOrderId: string
  }

  let {
    organizationId,
    organizationSlug = null,
    organizationResolvedId = null,
    workOrderId,
  }: Props = $props()
  const authContext = getKehrblattControlAuthContext()
  const permissions = $derived($authContext?.permissions ?? NO_PERMISSIONS)
  const currentUserId = $derived($authContext?.currentUserId ?? null)
  const canViewScheduling = $derived(
    permissions.allows("kehrblatt.scheduling", "view"),
  )
  const uuidPattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
  const isUuid = (value: string | null | undefined) => Boolean(value && uuidPattern.test(value))

  let workOrder = $state<WorkOrder | null>(null)
  let workOrderSettledFor = $state<string | null>(null)
  const organizationWorkspace = getOrganizationWorkspace()
  let standaloneOrganization = $state<OrganizationFrame | null>(null)
  const resolvedOrganization = $derived.by(() => {
    if (!organizationWorkspace) return standaloneOrganization

    const organization = organizationWorkspace.getOrganization()
    const identifiers = [organizationId, organizationResolvedId, organizationSlug]

    return organization &&
      identifiers.some((id) => id === organization.id || id === organization.slug)
      ? organization
      : null
  })
  let properties = $state<Property[]>([])
  let scheduleEvents = $state<ScheduleEvent[]>([])
  let reloadKey = $state(0)
  const decodeSegment = (value: string | null | undefined) => {
    if (typeof value !== "string") return null

    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const pathOrgSegment = $derived.by(() => {
    const segments = normalizedPath.split("/").filter(Boolean)

    if (segments[0] === "organizations" && segments[1]) {
      return decodeSegment(segments[1])
    }

    return null
  })

  const organizationIdentifier = $derived(
    organizationResolvedId ?? organizationId ?? organizationSlug ?? pathOrgSegment ?? null,
  )
  const apiOrganizationId = $derived(
    resolvedOrganization?.id ??
      organizationResolvedId ??
      (isUuid(organizationId) ? organizationId : null),
  )
  const effectiveOrganizationId = $derived(apiOrganizationId ?? "")
  const organizationPathSegment = $derived(
    resolvedOrganization?.slug ?? organizationSlug ?? pathOrgSegment ?? organizationId ?? null,
  )
  const brandingRouteScopeKey = $derived(
    organizationPathSegment ? `organization:${organizationPathSegment}` : null,
  )

  const basePath = $derived(
    organizationPathSegment && workOrderId
      ? `/organizations/${encodeURIComponent(organizationPathSegment)}/work-orders/${encodeURIComponent(workOrderId)}`
      : "",
  )
  const tabPaths = $derived(buildWorkOrderDetailViewPaths(basePath))
  const normalizedPath = $derived(normalizePath($location.path))
  const onPlanningRoute = $derived(
    Boolean(tabPaths.contactPlanning && normalizedPath.startsWith(tabPaths.contactPlanning)),
  )
  const onLegacyDetailsRoute = $derived(
    Boolean(tabPaths.details && normalizedPath.startsWith(tabPaths.details)),
  )
  const onLegacyLetterRoute = $derived(
    Boolean(tabPaths.letter && normalizedPath.startsWith(tabPaths.letter)),
  )

  $effect(() => {
    if (!canViewScheduling || !basePath || !tabPaths.contactPlanning) return

    if (normalizedPath === basePath) {
      spaNavigate(tabPaths.contactPlanning, { replace: true, noSearchParam: false })
    }
  })

  $effect(() => {
    if (!canViewScheduling || !onLegacyDetailsRoute || !tabPaths.contactPlanning) return

    spaNavigate(tabPaths.contactPlanning, { replace: true, noSearchParam: false })
  })

  $effect(() => {
    if (!canViewScheduling || !onLegacyLetterRoute || !tabPaths.contactPlanning) return

    spaNavigate(tabPaths.contactPlanning, { replace: true, noSearchParam: false })
  })

  let lastAccessRedirectPath = $state<string | null>(null)
  $effect(() => {
    if (!$authContext || canViewScheduling || !organizationPathSegment) return

    const redirectPath = `/organizations/${encodeURIComponent(organizationPathSegment)}/overview`

    if (lastAccessRedirectPath === redirectPath) return

    lastAccessRedirectPath = redirectPath
    newNotification({
      class: "info",
      html: "Für diesen Auftrag fehlt Ihnen die Berechtigung.",
    })
    spaNavigate(redirectPath, { replace: true, noSearchParam: true })
  })

  const orgBillingPlanState = $derived($orgPlanLookup(effectiveOrganizationId))
  const orgBilling = $derived(orgBillingPlanState?.billing ?? null)
  const orgBillingResolved = $derived(
    orgBillingPlanState?.loadedAt !== null &&
      orgBillingPlanState?.loadedAt !== undefined &&
      !orgBillingPlanState?.error,
  )
  const subscriptionRestriction = $derived(
    resolveOrganizationSubscriptionRestriction({
      organization: resolvedOrganization,
      billingAvailable: orgBillingResolved,
      billing: orgBilling,
    }),
  )
  const subscriptionRestrictionActive = $derived(subscriptionRestriction.active)
  const contactFlowsLocked = $derived(
    Boolean(workOrder) ? workOrder?.status !== "OPEN" || subscriptionRestrictionActive : false,
  )
  const detailTitle = $derived(
    workOrder?.order_number?.trim() ? `Auftrag ${workOrder.order_number}` : "Auftrag",
  )
  const detailStatusCode = $derived.by(() =>
    workOrder ? ((workOrder as any).view_status ?? null) : null,
  )
  const detailStatusLabel = $derived(
    detailStatusCode ? (workOrderStatusLabels[detailStatusCode] ?? detailStatusCode) : "",
  )
  const propertyById = $derived(
    Object.fromEntries(properties.map((property) => [property.id, property])),
  )
  const detailSubtitle = $derived.by(() => {
    if (!workOrder) return "Auftragsdetails"

    const property = propertyById[workOrder.property_id]
    const address = property ? formatAddress(property) : "Keine Liegenschaft"
    const base = detailStatusLabel ? `${address} • ${detailStatusLabel}` : address

    if (!workOrder.archived_at) return base

    return `${base} • ${archivePresentation(workOrder.archived_at, workOrder.archive_reason)}`
  })

  const assistTaskHint = $derived(onPlanningRoute ? "CONTACT_PLANNING" : "WORK_ORDER_DETAILS")

  $effect(() => {
    if (!effectiveOrganizationId) return

    void ensureOrgBillingPlan(effectiveOrganizationId, { maxAgeMs: 60_000 })
  })

  const loadOrganizationContext = async (identifier: string, isCurrent: () => boolean) => {
    try {
      const organization = await fetchOrganizationFrame(identifier)

      if (!isCurrent()) return

      standaloneOrganization = organization
    } catch (err) {
      if (!isCurrent()) return

      console.error("work_order_detail_org_load_failed", err)
      standaloneOrganization = null
    }
  }

  $effect(() => {
    if (organizationWorkspace) return

    const identifier = organizationIdentifier

    if (!identifier) return

    let current = true
    standaloneOrganization = null
    void loadOrganizationContext(identifier, () => current)

    return () => {
      current = false
    }
  })

  const handleContextChange = (payload: {
    workOrder?: WorkOrder | null
    properties?: Property[]
    scheduleEvents?: ScheduleEvent[]
  }) => {
    if (payload.workOrder !== undefined) {
      workOrder = payload.workOrder
      workOrderSettledFor = workOrderId
    }

    if (payload.properties) {
      properties = payload.properties
    }

    if (payload.scheduleEvents) {
      scheduleEvents = payload.scheduleEvents
    }
  }

  const viewFrameContext = $derived.by<WorkOrdersViewFrameContext>(() => ({
    permissions,
    organizationId: effectiveOrganizationId,
    organizationSlug: organizationPathSegment,
    workOrderId,
    workOrder,
    properties,
    reloadKey,
    subscriptionRestrictionActive,
    currentUserId,
    onContextChange: handleContextChange,
  }))
  const workOrderArchived = $derived(Boolean(workOrder?.archived_at))
  const confirmedWorkOrder = $derived(
    workOrder?.id === workOrderId && workOrder?.organization_id === effectiveOrganizationId
      ? workOrder
      : null,
  )
  // The record frame stands from the route on; the loaded work order only fills the header.
  const recordDetail = $derived<RecordDetailConfig | undefined>(
    canViewScheduling && organizationId && workOrderId
      ? {
          key: `work-order:${organizationId}:${workOrderId}`,
          loading: !confirmedWorkOrder && workOrderSettledFor !== workOrderId,
          header: confirmedWorkOrder
            ? buildWorkOrderHeader(confirmedWorkOrder, properties)
            : { icon: workOrderRecordIcon, fields: [] },
        }
      : undefined,
  )
  const navigationContext = $derived.by(() => ({
    permissions,
    currentUserId,
    workOrderArchived,
  }))

  $effect(() => {
    if (!workOrder || !effectiveOrganizationId) return

    updateAssistContext({
      page: "WORK_ORDER_DETAIL",
      taskHint: assistTaskHint,
      organizationId: effectiveOrganizationId,
      workOrderId: workOrder.id,
      propertyId: workOrder.property_id ?? null,
    })
  })

  type AssistEventPayload = {
    name?: string
    normalizedName?: string
    parameters?: Record<string, unknown>
  }

  const matchesWorkOrder = (payload: AssistEventPayload) => {
    const params = payload.parameters ?? {}
    const targetOrg =
      (params.organization_id as string | undefined) ??
      (params.organizationId as string | undefined) ??
      (params.org_id as string | undefined)

    if (targetOrg && effectiveOrganizationId && targetOrg !== effectiveOrganizationId) {
      return false
    }

    const targetOrder =
      (params.work_order_id as string | undefined) ??
      (params.workOrderId as string | undefined) ??
      (params.order_id as string | undefined)

    if (targetOrder && workOrderId && targetOrder !== workOrderId) {
      return false
    }

    const normalized = (payload.normalizedName ?? payload.name ?? "").toLowerCase()

    return Boolean(
      normalized.includes("work_order") ||
      normalized.includes("order") ||
      normalized.includes("case_communication"),
    )
  }

  const registerAssistHandler = () => {
    const handlerId = `work-order-detail-${workOrderId}`
    const handler = (payload: AssistEventPayload) => {
      if (!matchesWorkOrder(payload)) return

      reloadKey += 1
    }
    registerEventCallback("assist.event", handlerId, handler)
    const stop = () => unregisterEventCallback("assist.event", handlerId)

    return stop
  }

  let unregisterAssist: (() => void) | null = null
  onMount(() => {
    unregisterAssist = registerAssistHandler()
  })
  onDestroy(() => unregisterAssist?.())

  const pageNavigation = $derived.by(() =>
    buildWorkOrderDetailViewNavigation({
      context: navigationContext,
      paths: tabPaths,
      capabilities: { contactFlowsLocked },
      snippets: {
        schedulePlanning: schedulePlanningPageContent,
      },
    }),
  )
  const schedulePlanningTabs = $derived.by(
    () => pageNavigation.find((page) => page.path === tabPaths.contactPlanning)?.tabs ?? [],
  )
</script>

<OrgRootBrandingMount
  organizationId={effectiveOrganizationId}
  routeScopeKey={brandingRouteScopeKey}
  source="kehrblatt-work-order-detail"
/>

{#snippet schedulePlanningPageContent(activeTab: string)}
  <SchedulePlanningPage {viewFrameContext} {activeTab} />
{/snippet}

{#if !pageNavigation.length}
  <div class="state-block">
    <p>Keine Berechtigung, um diesen Auftrag anzuzeigen.</p>
  </div>
{:else if onPlanningRoute && !schedulePlanningTabs.length}
  <div class="state-block">
    <p>Keine Berechtigung für Terminkommunikation.</p>
  </div>
{:else}
  <WorkOrderHeaderActions {viewFrameContext}>
    {#snippet children(actions)}
      <PageTemplate
        viewKey={`work-order:${organizationPathSegment ?? organizationId ?? "global"}:${workOrderId}`}
        title={detailTitle}
        subtitle={detailSubtitle}
        {pageNavigation}
        locationStore={location}
        gobackLink={organizationPathSegment
          ? `/organizations/${encodeURIComponent(organizationPathSegment)}/work-orders`
          : ""}
        helpModalTextId="workOrderDetails"
        recordDetail={recordDetail ? { ...recordDetail, actions } : undefined}
      />
    {/snippet}
  </WorkOrderHeaderActions>
{/if}

<style lang="less">
  .state-block {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;

    p {
      margin: 0;
    }
  }
</style>
